import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  FileSpreadsheet,
  History,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  Settings as SettingsIcon,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { STATUS_STYLES, absTime, inr, relTime } from "@/lib/format";
import { colLabel } from "@/lib/columns";
import { parseCsv, saveBlob } from "@/lib/csv";
import {
  errMessage,
  exportDownload,
  exportPreview,
  exportRuns,
  getSettings,
  listQueue,
  resetExportFlag,
} from "@/lib/api";

const StatusChip = ({ status, testId }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.needs_review;
  const Icon = status === "ready" ? CheckCircle2 : status === "exported" ? Download : AlertTriangle;
  return (
    <span
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold", s.cls)}
      data-testid={testId}
    >
      <Icon className="h-2.5 w-2.5" aria-hidden="true" />
      {s.label}
    </span>
  );
};

const StatCard = ({ label, value, tone = "default", testId }) => (
  <div
    className={cn(
      "rounded-xl border p-3",
      tone === "ready"
        ? "border-success-border bg-success-surface"
        : tone === "review"
          ? "border-warning-border bg-warning-surface"
          : tone === "exported"
            ? "border-info-border bg-info-surface"
            : "border-border bg-card"
    )}
  >
    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-0.5 font-mono text-xl font-semibold [font-variant-numeric:tabular-nums]" data-testid={testId}>
      {value}
    </p>
  </div>
);

export default function ExportPage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [settings, setSettings] = useState(null);
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [applyLabel, setApplyLabel] = useState(true);
  const [includeExported, setIncludeExported] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [queue, settingsPayload, runsPayload] = await Promise.all([
        listQueue({ status: "", q: "" }),
        getSettings(),
        exportRuns(),
      ]);
      setRows(queue.items || []);
      setCounts(queue.counts || {});
      setSettings(settingsPayload.settings || {});
      setApplyLabel(settingsPayload.settings?.auto_label !== false);
      setRuns(runsPayload.items || []);
    } catch (e) {
      toast.error("Could not load export data", { description: errMessage(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const term = q.trim().toLowerCase();
  const visible = useMemo(
    () =>
      rows.filter((r) => {
        if (r.status === "exported" && !includeExported) return false;
        if (!term) return true;
        return [r.company_name, r.pan, r.account_number, r.ifsc, r.approver_email, r.source_subject]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term));
      }),
    [rows, term, includeExported]
  );

  const exportable = useMemo(() => visible.filter((r) => r.status !== "needs_review"), [visible]);
  const blocked = useMemo(() => visible.filter((r) => r.status === "needs_review"), [visible]);
  const selectedRows = useMemo(() => rows.filter((r) => selected.includes(r.id)), [rows, selected]);
  const selectedTotal = selectedRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const allSelected = exportable.length > 0 && exportable.every((r) => selected.includes(r.id));

  const toggleRow = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAll = () =>
    setSelected(allSelected ? [] : Array.from(new Set(exportable.map((r) => r.id))));

  const enabledColumns = (settings?.csv_format || []).filter((c) => c.enabled !== false);

  const runPreview = async () => {
    if (!selected.length) {
      toast.error("Select at least one row to preview");
      return;
    }
    setPreviewing(true);
    try {
      const data = await exportPreview(selected);
      setPreview(data);
      toast.success("Preview ready", { description: `${data.row_count} row(s) in the Mysa layout.` });
    } catch (e) {
      toast.error("Could not build the preview", { description: errMessage(e) });
    } finally {
      setPreviewing(false);
    }
  };

  const runDownload = async () => {
    if (!selected.length) {
      toast.error("Select at least one row to export");
      return;
    }
    setDownloading(true);
    try {
      const res = await exportDownload(selected, applyLabel);
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(disposition);
      const filename =
        match?.[1] ||
        `${settings?.csv_filename_prefix || "mysa_vendors"}_${new Date().toISOString().slice(0, 10)}.csv`;
      saveBlob(res.data, filename);

      const labeled = res.headers?.["x-threads-labeled"];
      const labelErrors = res.headers?.["x-label-errors"];
      toast.success(`Exported ${selected.length} row(s)`, {
        description: [
          filename,
          labeled && labeled !== "0" ? `${labeled} Gmail thread(s) labelled` : null,
          labelErrors ? `Label note: ${labelErrors}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
      });
      setSelected([]);
      setPreview(null);
      load();
    } catch (e) {
      let description = errMessage(e);
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          description = JSON.parse(text)?.detail || description;
        } catch (parseError) {
          /* keep the fallback message */
        }
      }
      toast.error("Export failed", { description });
    } finally {
      setDownloading(false);
    }
  };

  const unmarkExported = async () => {
    const ids = selectedRows.filter((r) => r.status === "exported").map((r) => r.id);
    if (!ids.length) {
      toast.error("Select one or more exported rows first");
      return;
    }
    setResetting(true);
    try {
      await resetExportFlag(ids);
      toast.success(`${ids.length} row(s) can be exported again`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error("Could not reset those rows", { description: errMessage(e) });
    } finally {
      setResetting(false);
    }
  };

  const previewRows = useMemo(() => (preview?.csv ? parseCsv(preview.csv) : []), [preview]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="export-loading">
        <Skeleton className="h-9 w-56" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="export-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <FileSpreadsheet className="h-4 w-4 text-primary" aria-hidden="true" />
            Export to Mysa CSV
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Only rows that pass validation can be exported. Fix anything in Needs Review first.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            className="h-9 gap-1.5 bg-card text-xs"
            onClick={load}
            data-testid="export-refresh-button"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Refresh
          </Button>
          <Button asChild variant="outline" className="h-9 gap-1.5 bg-card text-xs">
            <Link to="/settings" data-testid="export-to-settings-link">
              <SettingsIcon className="h-3.5 w-3.5" aria-hidden="true" /> Edit CSV format
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Selected" value={selected.length} testId="export-selected-count" />
        <StatCard label="Ready" value={counts.ready ?? 0} tone="ready" testId="export-ready-count" />
        <StatCard label="Needs review" value={counts.needs_review ?? 0} tone="review" testId="export-review-count" />
        <StatCard label="Already exported" value={counts.exported ?? 0} tone="exported" testId="export-exported-count" />
      </div>

      {blocked.length ? (
        <Alert className="border-warning-border bg-warning-surface" data-testid="export-blocked-alert">
          <AlertTriangle className="h-4 w-4 text-warning-text" aria-hidden="true" />
          <AlertTitle className="text-[13px] text-warning-text">
            {blocked.length} row(s) are held back
          </AlertTitle>
          <AlertDescription className="text-xs text-warning-text">
            Rows in Needs Review cannot be selected or exported.{" "}
            <Link to="/queue" className="font-semibold underline underline-offset-2" data-testid="export-fix-in-queue-link">
              Fix them in the Queue
            </Link>{" "}
            - missing or invalid fields are listed there.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_380px]">
        {/* ----------------------------- row picker ----------------------------- */}
        <Card className="border-border bg-card shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]">
          <CardHeader className="gap-3 pb-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-[15px] font-semibold tracking-tight">Choose rows</CardTitle>
                <CardDescription className="text-xs">
                  {exportable.length} exportable · {blocked.length} blocked
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <Input
                    placeholder="Search vendor, PAN, approver"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="h-9 w-full pl-8 sm:w-64"
                    data-testid="export-search-input"
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-xs">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  disabled={!exportable.length}
                  data-testid="export-select-all"
                />
                Select all exportable
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-xs">
                <Checkbox
                  checked={includeExported}
                  onCheckedChange={(v) => setIncludeExported(!!v)}
                  data-testid="export-include-exported"
                />
                Show already exported
              </label>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {visible.length ? (
              <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border" data-testid="export-row-list">
                {visible.map((row) => {
                  const isBlocked = row.status === "needs_review";
                  const isSelected = selected.includes(row.id);
                  const issues = [
                    ...(row.missing_fields || []).map((f) => `${colLabel(f)} missing`),
                    ...(row.invalid_fields || []).map((f) => `${colLabel(f)} invalid`),
                  ];
                  return (
                    <li
                      key={row.id}
                      className={cn(
                        "flex items-start gap-3 px-3 py-2.5 transition-colors",
                        isBlocked
                          ? "bg-warning-surface"
                          : isSelected
                            ? "bg-success-surface"
                            : "bg-card hover:bg-secondary"
                      )}
                      data-testid={`export-row-${row.id}`}
                    >
                      <Checkbox
                        className="mt-0.5"
                        checked={isSelected}
                        disabled={isBlocked}
                        onCheckedChange={() => toggleRow(row.id)}
                        aria-label={`Select ${row.company_name || "vendor"}`}
                        data-testid={`export-row-checkbox-${row.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-[13px] font-medium" data-testid={`export-row-name-${row.id}`}>
                            {row.company_name || "(no company name)"}
                          </span>
                          <StatusChip status={row.status} testId={`export-row-status-${row.id}`} />
                          {row.source === "manual" ? (
                            <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                              Manual
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                          {[row.pan, row.account_number, row.ifsc].filter(Boolean).join(" · ") || "no bank details yet"}
                        </p>
                        {issues.length ? (
                          <p className="mt-0.5 text-[11px] text-warning-text" data-testid={`export-row-issues-${row.id}`}>
                            {issues.join(" · ")}
                          </p>
                        ) : null}
                        {row.source_subject ? (
                          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                            {row.source_subject}
                          </p>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-mono text-[13px] font-semibold [font-variant-numeric:tabular-nums]">
                          {inr(row.amount) ? `₹${inr(row.amount)}` : "-"}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {row.exported_at ? `exported ${relTime(row.exported_at)}` : relTime(row.created_at)}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div
                className="flex flex-col items-start gap-2 rounded-xl border border-dashed border-border p-6"
                data-testid="export-empty-state"
              >
                <FileSpreadsheet className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <p className="text-[13px] font-medium">Nothing to export yet</p>
                <p className="text-xs text-muted-foreground">
                  Extract vendors from the inbox or upload documents, then review them in the queue.
                </p>
                <div className="mt-1 flex gap-2">
                  <Button asChild size="sm" className="h-8 text-xs">
                    <Link to="/" data-testid="export-empty-inbox-link">
                      Open Inbox
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="h-8 bg-card text-xs">
                    <Link to="/queue" data-testid="export-empty-queue-link">
                      Open Queue
                    </Link>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ------------------------------ actions ------------------------------ */}
        <div className="space-y-4">
          <Card className="border-border bg-card shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-[15px] font-semibold tracking-tight">Export</CardTitle>
              <CardDescription className="text-xs">
                {enabledColumns.length} column(s) · prefix{" "}
                <code className="font-mono">{settings?.csv_filename_prefix || "mysa_vendors"}</code>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <div className="rounded-lg border border-border bg-secondary p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Rows selected</span>
                  <span className="font-mono font-semibold" data-testid="export-summary-rows">
                    {selected.length}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-muted-foreground">Total amount</span>
                  <span className="font-mono font-semibold" data-testid="export-summary-amount">
                    {selectedTotal ? `₹${inr(selectedTotal)}` : "-"}
                  </span>
                </div>
              </div>

              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border p-3">
                <Checkbox
                  checked={applyLabel}
                  onCheckedChange={(v) => setApplyLabel(!!v)}
                  data-testid="export-apply-label-checkbox"
                />
                <span className="text-xs">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Tag className="h-3 w-3" aria-hidden="true" /> Label the Gmail threads
                  </span>
                  <span className="mt-0.5 block text-muted-foreground">
                    Applies <code className="font-mono">{settings?.label_name || "Mysa-Processed"}</code> so the same
                    mail is not processed twice.
                  </span>
                </span>
              </label>

              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  className="h-9 gap-1.5 bg-card text-xs"
                  onClick={runPreview}
                  disabled={!selected.length || previewing}
                  data-testid="export-preview-button"
                >
                  {previewing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Preview CSV
                </Button>
                <Button
                  className="h-9 gap-1.5"
                  onClick={runDownload}
                  disabled={!selected.length || downloading}
                  data-testid="export-download-button"
                >
                  {downloading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Download CSV
                </Button>
                <Button
                  variant="ghost"
                  className="h-8 gap-1.5 text-xs"
                  onClick={unmarkExported}
                  disabled={resetting || !selectedRows.some((r) => r.status === "exported")}
                  data-testid="export-unmark-button"
                >
                  {resetting ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                  ) : (
                    <RotateCcw className="h-3 w-3" aria-hidden="true" />
                  )}
                  Allow re-export of selected
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-[15px] font-semibold tracking-tight">
                <History className="h-4 w-4 text-primary" aria-hidden="true" /> Export history
              </CardTitle>
              <CardDescription className="text-xs">Last {runs.length} export run(s)</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {runs.length ? (
                <ul className="space-y-2" data-testid="export-runs-list">
                  {runs.slice(0, 8).map((run) => (
                    <li
                      key={run.id}
                      className="rounded-lg border border-border p-2.5"
                      data-testid={`export-run-${run.id}`}
                    >
                      <p className="truncate font-mono text-[11px] font-medium">{run.filename}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {run.row_count} row(s) · {(run.label_applied || []).length} labelled ·{" "}
                        {absTime(run.created_at)}
                      </p>
                      {(run.label_errors || []).length ? (
                        <p className="mt-0.5 text-[11px] text-danger-text">{(run.label_errors || []).join("; ")}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="export-runs-empty">
                  No exports yet.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ------------------------------ CSV preview ------------------------------ */}
      {preview ? (
        <Card
          className="border-border bg-card shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]"
          data-testid="export-preview-card"
        >
          <CardHeader className="flex flex-col gap-2 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-[15px] font-semibold tracking-tight">CSV preview</CardTitle>
              <CardDescription className="text-xs">
                {preview.row_count} row(s) · {preview.columns?.length || 0} column(s) ·{" "}
                <code className="font-mono">{preview.filename}</code>
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              className="h-8 text-xs"
              onClick={() => setPreview(null)}
              data-testid="export-close-preview-button"
            >
              Close preview
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {(preview.not_ready || []).length ? (
              <Alert className="mb-3 border-danger-border bg-danger-surface" data-testid="export-preview-warning">
                <AlertTriangle className="h-4 w-4 text-danger-text" aria-hidden="true" />
                <AlertTitle className="text-[13px] text-danger-text">These rows will block the download</AlertTitle>
                <AlertDescription className="text-xs text-danger-text">
                  {(preview.not_ready || [])
                    .map(
                      (r) =>
                        `${r.company_name || "(no name)"}: ${[...(r.missing || []), ...(r.invalid || [])]
                          .map(colLabel)
                          .join(", ")}`
                    )
                    .join(" · ")}
                </AlertDescription>
              </Alert>
            ) : null}
            <ScrollArea className="max-h-[420px] w-full rounded-xl border border-border">
              <table className="vde-table w-full border-collapse text-xs" data-testid="export-preview-table">
                <thead>
                  <tr>
                    {(previewRows[0] || []).map((h, i) => (
                      <th
                        key={`${h}-${i}`}
                        className="whitespace-nowrap border-b border-border bg-card px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.slice(1).map((r, ri) => (
                    <tr key={`row-${ri}`} className={ri % 2 ? "bg-secondary" : "bg-card"}>
                      {r.map((cell, ci) => (
                        <td
                          key={`cell-${ri}-${ci}`}
                          className="whitespace-nowrap border-b border-border px-3 py-1.5 font-mono text-[11px] [font-variant-numeric:tabular-nums]"
                        >
                          {cell || <span className="text-muted-foreground">-</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <Separator className="my-3" />
            <details data-testid="export-raw-csv">
              <summary className="cursor-pointer text-xs font-medium text-primary">
                View raw CSV text
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-secondary p-3 font-mono text-[11px] leading-relaxed">
                {preview.csv}
              </pre>
            </details>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
